import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { Button } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useSurveyData } from "../../../../../hooks/useSurveyData";

const SURVEY_TABLE_HEADERS = [
  {
    columnKey: "surveyName",
    header: "Survey Name",
    className: "survey-list-table__col-name",
  },
  { columnKey: "surveyId", header: "ID", className: "survey-list-table__col-id" },
  {
    columnKey: "sourcePartner",
    header: "Source",
    className: "survey-list-table__col-source",
  },
  {
    columnKey: "surveyUrl",
    header: "Survey URL",
    className: "survey-list-table__col-url",
  },
];

export default function SurveyListTable({
  onSurveySelect,
  onBack,
  selectedSurveyId,
}) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSearchFields, setShowSearchFields] = useState(false);

  const {
    paginatedSurveys,
    loading,
    error,
    apiErrors,
    fetchSurveys,
    filters,
    updateFilters,
    resetFilters,
    sourcePartners,
    pagination,
    resetPage,
    sortConfig,
    setSortConfig,
  } = useSurveyData(rowsPerPage);

  const [page, setPage] = useState(pagination.currentPage - 1);

  const sourcePartnerOptions = useMemo(
    () => [...new Set(sourcePartners.filter(Boolean))],
    [sourcePartners]
  );

  useEffect(() => {
    setPage(pagination.currentPage - 1);
  }, [pagination.currentPage]);

  useEffect(() => {
    fetchSurveys();
  }, [rowsPerPage, fetchSurveys]);

  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const handleToggleSearchFields = () => {
    setShowSearchFields(!showSearchFields);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    pagination.goToPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    pagination.goToPage(1);
  };

  if (error) {
    return (
      <div className="survey-list-table__error" data-testid="survey-list-error">
        <p>Failed to load surveys. Please try again.</p>
        <Button
          variant="secondary"
          text="Retry"
          size="small"
          onClick={() => fetchSurveys()}
        />
      </div>
    );
  }

  return (
    <div className="survey-list-table" data-testid="survey-list-table">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Typography variant="h6" style={{ fontSize: "22px", fontWeight: 700 }}>
          Add Condition
        </Typography>
        <IconButton onClick={onBack} data-testid="close-button">
          <CloseIcon />
        </IconButton>
      </div>
      <div className="survey-list-table__filters">
        <Select
          value={filters.sourcePartner}
          onChange={(e) => updateFilters({ sourcePartner: e.target.value })}
          displayEmpty
          size="small"
          data-testid="survey-source-filter"
          sx={{ minWidth: 160, borderRadius: "24px" }}
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: "#999" }}>Sources</span>;
            }
            if (selected === "all") {
              return "ALL SOURCES";
            }
            return selected;
          }}
        >
          <MenuItem value="all">ALL SOURCES</MenuItem>
          {sourcePartnerOptions.map((partner) => (
            <MenuItem key={partner} value={partner}>
              {partner}
            </MenuItem>
          ))}
        </Select>
        {showSearchFields && (
          <>
            <TextField
              placeholder="Search by survey name"
              size="small"
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              data-testid="survey-search-input"
              sx={{ flex: 1 }}
            />
            <TextField
              placeholder="Search by ID"
              size="small"
              value={filters.searchId || ""}
              onChange={(e) => updateFilters({ searchId: e.target.value })}
              data-testid="survey-id-search-input"
              sx={{ flex: 1 }}
            />
          </>
        )}

        <div className="survey-list-table__filters-actions">
          <IconButton
            aria-label="filter"
            color="primary"
            size="medium"
            onClick={handleToggleSearchFields}
            data-testid="toggle-search-fields-button"
          >
            <FilterListIcon fontSize="inherit" />
          </IconButton>
          <Button
            variant="tertiary"
            iconLeading="refresh"
            text="Refresh Surveys"
            size="medium"
            onClick={() => {
              resetPage();
              resetFilters();
              fetchSurveys();
            }}
            data-testid="refresh-surveys-button"
          />
        </div>
      </div>

      {apiErrors?.length > 0 && (
        <div
          className="survey-list-table__api-errors"
          data-testid="survey-api-errors"
          style={{
            padding: "8px 12px",
            marginBottom: "12px",
            backgroundColor: "#fff3e0",
            borderRadius: "4px",
            border: "1px solid #ffb74d",
          }}
        >
          {apiErrors.map((err, idx) => (
            <p key={idx} style={{ margin: 0, fontSize: "14px", color: "#e65100" }}>
              {err.message} ({err.code})
            </p>
          ))}
        </div>
      )}

      {loading ? (
        <div
          className="survey-list-table__loading"
          data-testid="survey-list-loading"
        >
          <CircularProgress size={48} />
        </div>
      ) : (
        <div>
          <div className="survey-list-table__body">
            <table
              className="common-table survey-list-table__table"
              data-testid="table"
            >
              <thead>
                <tr>
                  {SURVEY_TABLE_HEADERS.map((h) => (
                    <th
                      key={h.columnKey}
                      className={h.className}
                      onClick={() => handleSort(h.columnKey)}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "4px" }}
                      >
                        {h.header}
                        {sortConfig.key === h.columnKey &&
                          (sortConfig.direction === "asc" ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ))}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && paginatedSurveys.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="survey-list-table__empty">
                      No surveys found with available questions
                    </td>
                  </tr>
                ) : (
                  paginatedSurveys.map((survey) => (
                    <tr
                      key={survey.surveyId}
                      className={`survey-list-table__row ${selectedSurveyId === survey.surveyId ? "selected" : ""}`}
                      onClick={() => onSurveySelect(survey)}
                      data-testid={`survey-row-${survey.surveyId}`}
                    >
                      <td>{survey.surveyName || "-"}</td>
                      <td>{survey.surveyDisplayId || "-"}</td>
                      <td>{survey.sourcePartner || "-"}</td>
                      <td>{survey.surveyUrl || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="survey-list-table__pagination">
            <TablePagination
              component="div"
              count={pagination.totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              showFirstButton
              showLastButton
            />
          </div>
        </div>
      )}
    </div>
  );
}

SurveyListTable.propTypes = {
  onSurveySelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  selectedSurveyId: PropTypes.string,
};
